<template>
  <div class="relative z-10">
    <!-- Improved Sidebar -->
    <div
      :class="[
        'relative min-h-screen bg-white/80 transition-all duration-500 shadow-sm shadow-slate-100/40 overflow-hidden flex flex-col border-r border-slate-100/50 backdrop-blur-sm',
        { 'w-[64px]': isIconOnly, 'w-[180px]': !isIconOnly }
      ]"
    >
      <!-- Sidebar Header with Gradient Effect -->
      <div class="h-14 flex items-center justify-start border-b border-slate-100/50 px-2.5" data-tour="sidebar-logo">
        <transition name="fade" mode="out-in">
          <img
            v-if="isIconOnly"
            key="icon-logo"
            class="w-10 h-10 object-contain"
            src="../../../assets/logo.png"
            alt="Active Digital Logo"
          />
          <img
            v-else
            key="full-logo"
            class="h-10 object-contain"
            src="../../../assets/header/logo.png"
            alt="Boss AI Logo"
          />
        </transition>
      </div>

      <!-- Menu Links with Improved Styling -->
      <div class="space-y-1.5 px-2.5 py-3 flex-grow" data-tour="sidebar-menu">
        <transition-group name="menu-item" tag="div" class="space-y-1">
          <div v-for="item in visibleMenuItems" :key="item.name" class="relative" :ref="el => setMenuItemRef(item.name, el)">
            <div
              @click="handleItemClick(item)"
              :title="isIconOnly ? item.name : ''"
              :class="[
                'flex items-center justify-between rounded-lg px-2.5 py-2 text-sm transition-all duration-300 select-none',
                {
                  'bg-[linear-gradient(135deg,var(--brand-500),var(--brand-700))] text-white shadow-sm shadow-indigo-500/20':
                    isActiveMainMenuItem(item),
                  'hover:bg-slate-50 hover:text-slate-900':
                    !isActiveMainMenuItem(item) && !item.disabled,
                  'cursor-pointer': !item.disabled,
                  'cursor-not-allowed opacity-50': item.disabled
                }
              ]"
            >
              <div class="flex items-center gap-2.5" :class="item.disabled && 'opacity-25'">
                <div class="w-4 flex-shrink-0">
                  <component
                    :is="item.icon"
                    :class="[
                      'mx-auto h-4 w-4 transition-all duration-300',
                      {
                        'text-white': isActiveMainMenuItem(item),
                        'text-slate-400': !isActiveMainMenuItem(item),
                        'opacity-40': item.disabled
                      }
                    ]"
                  />
                </div>

                <transition name="slide-fade">
                  <p
                    v-if="!isIconOnly"
                    :class="[
                      'whitespace-nowrap transition-all duration-300 select-none font-medium',
                      {
                        'text-white': isActiveMainMenuItem(item),
                        'text-slate-600': !isActiveMainMenuItem(item)
                      }
                    ]"
                  >
                    {{ item.name }}
                  </p>
                </transition>
              </div>

              <!-- Right arrow icon -->
              <div v-if="!isIconOnly && item.subItems" class="flex items-center">
                <span
                  :class="[
                    'transition-transform duration-300 select-none',
                    {
                      'transform rotate-90': item.isOpen,
                      'text-white': isActiveMainMenuItem(item),
                      'text-slate-300': !isActiveMainMenuItem(item)
                    }
                  ]"
                >
                  <ChevronRight class="h-3.5 w-3.5" />
                </span>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Footer Section with Collapse Button and Logout -->
      <div class="p-2.5 border-t border-slate-100/50 space-y-1.5">
        <!-- Tour Button -->
        <!-- <button
          @click="startSidebarTour"
          :title="isIconOnly ? 'Rehber Turu' : ''"
          class="w-full flex items-center justify-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-none outline-none focus:ring-0 hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 focus:outline-none text-xs font-semibold shadow-sm hover:shadow-md"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span v-if="!isIconOnly">Rehber</span>
        </button> -->

        <!-- Collapse Button -->
        <button
          @click="toggleIconMode"
          :title="isIconOnly ? 'Genişlet' : 'Daralt'"
          class="w-full flex items-center justify-between rounded-lg px-2.5 py-2 cursor-pointer bg-white border-none outline-none focus:ring-0 hover:bg-slate-50 transition-all duration-300 focus:outline-none"
          data-tour="sidebar-collapse"
        >
          <div class="flex items-center space-x-2.5">
            <div class="w-4 flex-shrink-0">
              <svg class="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                <path fill-rule="evenodd" d="M6 5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2V5H6Zm4 0v14h8a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-8ZM3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <transition name="slide-fade">
              <p
                v-if="!isIconOnly"
                class="text-sm whitespace-nowrap transition-all duration-300 text-slate-600 font-medium focus:outline-none focus:ring-0 border-none"
              >
                Daralt
              </p>
            </transition>
          </div>
        </button>

        <!-- Logout Button -->
        <button
          @click="handleLogout"
          :title="isIconOnly ? 'Çıkış Yap' : ''"
          class="w-full flex items-center justify-between rounded-lg px-2.5 py-2 cursor-pointer bg-white border-none outline-none focus:ring-0 hover:bg-red-50 transition-all duration-300 focus:outline-none"
        >
          <div class="flex items-center space-x-2.5">
            <div class="w-4 flex-shrink-0">
              <LogOut class="h-4 w-4 text-red-400" />
            </div>
            <transition name="slide-fade">
              <p
                v-if="!isIconOnly"
                class="text-sm whitespace-nowrap transition-all duration-300 text-red-500 font-medium focus:outline-none focus:ring-0 border-none"
              >
                Çıkış Yap
              </p>
            </transition>
          </div>
        </button>
      </div>
    </div>

    <!-- Click catcher outside menu -->
    <div v-if="anySubMenuOpen" class="fixed inset-0 z-10" @click="closeAllSubmenus"></div>

    <!-- Tour Tooltip -->
    <TourTooltip
      v-model="showTour"
      :steps="sidebarTourSteps"
      @complete="onTourComplete"
      @skip="onTourSkip"
    />

    <!-- Submenu with improved styling -->
    <transition name="submenu" v-for="item in visibleMenuItems" :key="item.name">
      <div
        v-if="item.isOpen && hasVisibleSubItems(item)"
        class="fixed bg-white rounded-lg shadow-md shadow-slate-100/50 overflow-hidden z-30 border border-slate-100"
        :style="{
          top: getSubmenuPosition(item),
          left: isIconOnly ? '4.5rem' : '11.5rem'
        }"
      >
        <div class="py-1.5 min-w-[160px]">
          <div
            v-for="subItem in getVisibleSubItems(item)"
            :key="subItem.route"
            @click="navigateToSubItemAndClose(subItem.route, item)"
            class="flex items-center px-3.5 py-2 text-sm hover:bg-slate-50 transition-all duration-300 cursor-pointer"
            :class="[
              {
                'text-white bg-[linear-gradient(135deg,var(--brand-500),var(--brand-700))] font-medium mx-2 rounded-md':
                  isActiveRoute(subItem.route) || isRouteStartsWith(subItem.route),
                'text-slate-600 font-medium': !isActiveRoute(subItem.route) && !isRouteStartsWith(subItem.route),
                'cursor-not-allowed opacity-50': subItem.disabled,
                'hover:bg-transparent': subItem.disabled
              }
            ]"
          >
            {{ subItem.name }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, onBeforeUnmount } from 'vue';
import type { Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Swal from 'sweetalert2';

// İmport ettiğimiz logout fonksiyonunu, özel olarak oluşturulan bir isLoggingOut bayrağıyla birlikte kullanmak için
import { useAxios } from '../../../composables/axios';
const { logout } = useAxios();

import {
  ChevronRight,
  Database,
  FileText,
  LayoutDashboard,
  LogOut,
  // Menu,
  MessageSquare,
  Plug,
  Settings,
  Shield,
  Zap
} from 'lucide-vue-next';
import { useAuthStore } from '@/stores/auth';
import type { PermissionCode } from '@/constants/permissions';
import { useTour } from '@/composables/useTour';
import type { TourStep } from '@/composables/useTour';
import TourTooltip from '@/components/tour/TourTooltip.vue';

const { hasCompletedTour, startTour, completeTour } = useTour();

// Type definitions
interface SubMenuItem {
  name: string;
  route: string;
  disabled?: boolean;
  requiredPermissions?: PermissionCode | PermissionCode[];
}

interface MenuItem {
  name: string;
  route?: string;
  icon: Component;
  isOpen: boolean;
  disabled?: boolean;
  subItems?: SubMenuItem[];
  requiredPermissions?: PermissionCode | PermissionCode[];
}

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const canAccess = (requiredPermissions?: PermissionCode | PermissionCode[]) => {
  if (!requiredPermissions) return true;
  return authStore.hasAnyPermission(requiredPermissions);
};

// Default to icon-only mode
const isIconOnly = ref(false);

// Store menu item refs for positioning
const menuItemRefs = ref<Record<string, HTMLElement | null>>({});

const setMenuItemRef = (itemName: string, el: any) => {
  if (el) {
    menuItemRefs.value[itemName] = el as HTMLElement;
  }
};

// Props and Emits definitions
const emit = defineEmits(['update:isOpen', 'sidebarResize']);

// Tour state
const showTour = ref(false);

// Tour steps definition
const sidebarTourSteps: TourStep[] = [
  {
    target: '[data-tour="sidebar-logo"]',
    title: 'BOSS AI\'ya Hoş Geldiniz! 👋',
    description: 'Bu platformda yapay zeka destekli iş süreçlerinizi yönetebilir, raporlar oluşturabilir ve verilerinizi analiz edebilirsiniz.',
    placement: 'right'
  },
  {
    target: '[data-tour="sidebar-menu"]',
    title: 'Navigasyon Menüsü 🧭',
    description: 'Dashboard, AI Sohbet, Aksiyon, Entegrasyonlar ve daha fazlasına buradan ulaşabilirsiniz. Her modül farklı bir özellik sunar.',
    placement: 'right'
  },
  {
    target: '[data-tour="sidebar-collapse"]',
    title: 'Menü Kontrolü ↔️',
    description: 'Daha geniş bir çalışma alanı mı istiyorsunuz? Bu butona tıklayarak menüyü daraltıp genişletebilirsiniz.',
    placement: 'right'
  }
];

// Start tour function
const startSidebarTour = () => {
  showTour.value = true;
};

const onTourComplete = () => {
  completeTour('sidebar');
  showTour.value = false;
};

const onTourSkip = () => {
  completeTour('sidebar');
  showTour.value = false;
};

// Notify parent of default small sidebar state when component is mounted
onMounted(() => {
  emit('sidebarResize', isIconOnly.value);
  document.addEventListener('keydown', handleEscapeKey);

  // Start tour if not completed using queue system
  setTimeout(() => {
    if (!hasCompletedTour('sidebar')) {
      startTour('sidebar', startSidebarTour);
    }
  }, 1500);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});

// Close all menus when Escape key is pressed
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeAllSubmenus();
  }
};

// Filter menu items by access rights
const visibleMenuItems = computed(() =>
  menuItems.filter(item => canAccess(item.requiredPermissions))
);

// Check if any submenu is open
const anySubMenuOpen = computed(() => {
  return visibleMenuItems.value.some(item => item.isOpen);
});

// Close all submenus
const closeAllSubmenus = () => {
  menuItems.forEach(item => {
    item.isOpen = false;
  });
};

const getVisibleSubItems = (item: MenuItem): SubMenuItem[] => {
  if (!item.subItems) return [];
  return item.subItems.filter(subItem => canAccess(subItem.requiredPermissions));
};

const hasVisibleSubItems = (item: MenuItem) => getVisibleSubItems(item).length > 0;

const toggleIconMode = () => {
  isIconOnly.value = !isIconOnly.value;
  emit('sidebarResize', isIconOnly.value);
  closeAllSubmenus();
};

const handleItemClick = (item: MenuItem) => {
  if (!canAccess(item.requiredPermissions)) {
    return;
  }

  if (item.disabled) {
    return;
  }

  if (item.subItems) {
    if (!hasVisibleSubItems(item)) {
      return;
    }

    // Close other open menus
    menuItems.forEach(menuItem => {
      if (menuItem !== item) menuItem.isOpen = false;
    });
    item.isOpen = !item.isOpen;
  } else if (item.route) {
    router.push(item.route);
  }
};

// Navigate to submenu item and close menu
const navigateToSubItemAndClose = (routePath: string, parentItem: MenuItem) => {
  const subItem = parentItem.subItems?.find(item => item.route === routePath);
  if (!subItem || subItem.disabled || !canAccess(subItem.requiredPermissions)) {
    return;
  }

  router.push(routePath);
  parentItem.isOpen = false;
};

const handleLogout = async () => {
  try {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Çıkış yapmak istediğinize emin misiniz?',
      showCancelButton: true,
      confirmButtonText: 'Evet, çıkış yap',
      cancelButtonText: 'İptal',
      background: 'white',
      confirmButtonColor: '#4000F0',
      iconColor: '#4000F0'
    });

    if (!result.isConfirmed) return;

    await logout();

    await Swal.fire({
      icon: 'success',
      title: 'Başarıyla çıkış yapıldı',
      text: 'Çıkış işleminiz tamamlandı.',
      background: 'white',
      confirmButtonColor: '#4000F0',
      iconColor: '#4000F0'
    });

    router.push('/');
  } catch (error) {
    console.error('Logout failed:', error);
    await Swal.fire({
      icon: 'error',
      title: 'Çıkış Başarısız',
      text: 'Çıkış işlemi başarısız oldu. Lütfen tekrar deneyin.',
      background: 'white',
      confirmButtonColor: '#4000F0',
      iconColor: '#4000F0'
    });
  }
};

const getSubmenuPosition = (item: MenuItem) => {
  const element = menuItemRefs.value[item.name];
  if (element) {
    const rect = element.getBoundingClientRect();
    return `${rect.top}px`;
  }
  // Fallback to old calculation if ref not found
  const menuIndex = visibleMenuItems.value.indexOf(item);
  return `${58 + menuIndex * 40}px`;
};

// Route kontrolü için fonksiyon - tam eşleşme
const isActiveRoute = (routePath: string) => {
  return route.path === routePath;
};

// Route'un başlangıcına göre kontrol eden fonksiyon
const isRouteStartsWith = (routePath: string) => {
  return route.path.startsWith(routePath);
};

// Ana menü elemanının aktif olup olmadığını kontrol eden fonksiyon
const isActiveMainMenuItem = (item: MenuItem) => {
  // Eğer ana menü elemanının kendisi bir route'a sahipse ve o route aktifse
  if (item.route && isRouteStartsWith(item.route)) {
    return true;
  }
  
  // Özel kontroller - belirli route'lar için özel eşleştirmeler
  if (item.route === '/integrations') {
    // integrations ile ilgili tüm route'lar
    const integrationsRoutes = ['/integrations', '/integrationSetup'];
    return integrationsRoutes.some(routePath => 
      route.path === routePath || route.path.startsWith(routePath + '/')
    );
  }
  
  // Eğer alt menü elemanlarından herhangi biri aktifse (başlangıç kontrolü ile)
  if (item.subItems) {
    return item.subItems.some(subItem => isRouteStartsWith(subItem.route));
  }
  
  return false;
};

// Eski isActiveSubmenu fonksiyonunu kaldırıp yerine isActiveMainMenuItem kullanıyoruz

// Menu items
const menuItems = reactive<MenuItem[]>([
  {
    name: 'Dashboard',
    route: '/dashboard',
    icon: LayoutDashboard,
    isOpen: false,
    requiredPermissions: 'DASHBOARD_VIEW'
  },
  {
    name: 'AI Sohbet',
    route: '/main',
    icon: MessageSquare,
    isOpen: false
  },
  {
    name: 'Aksiyon',
    icon: Zap,
    isOpen: false,
    requiredPermissions: [
      'ACTION_VIEW',
      'OPERATION_VIEW',
      'FREQUENCY_VIEW'
    ],
    subItems: [
      { name: 'Raporlar', route: '/actions', requiredPermissions: 'ACTION_VIEW' },
      { name: 'Süreçler', route: '/processes', requiredPermissions: ['PROCESS_VIEW'] },
      { name: 'İşlemler', route: '/operations', requiredPermissions: 'OPERATION_VIEW' },
      { name: 'Sıklık', route: '/frequency', requiredPermissions: ['FREQUENCY_VIEW', 'FREQUENCY_CREATE', 'FREQUENCY_UPDATE', 'FREQUENCY_DELETE'] }
    ]
  },
  {
    name: 'Dökümanlar',
    route: '/documentPage',
    icon: FileText,
    isOpen: false,
    disabled: false,
    requiredPermissions: 'DOCUMENT_VIEW'
  },
  {
    name: 'Entegrasyonlar',
    route: '/integrations',
    icon: Plug,
    requiredPermissions: 'INTEGRATION_VIEW',
    isOpen: false
  },
  {
    name: 'SQL Builder',
    route: '/sql-builder',
    icon: Database,
    requiredPermissions: 'SQL_BUILDER_FULL_ACCESS',
    isOpen: false
  },
  {
    name: 'Yönetim',
    icon: Shield,
    isOpen: false,
    requiredPermissions: [
      'USER_ADMIN_READ'
    ],
    subItems: [
      {
        name: 'Kullanıcılar',
        route: '/admin/users',
        requiredPermissions: ['USER_ADMIN_READ']
      },
      // {
      //   name: 'Geri Bildirimler',
      //   route: '/admin/feedbacks',
      //   requiredPermissions: 'CONVERSATION_FEEDBACK_READ'
      // },
      {
        name: 'Roller',
        route: '/admin/roles',
        requiredPermissions: 'ROLE_VIEW'
      },
      {
        name: 'Departmanlar',
        route: '/admin/departments',
        requiredPermissions: ['DEPARTMENT_VIEW']
      }
    ]
  },
  {
    name: 'Ayarlar',
    icon: Settings,
    isOpen: false,
    subItems: [
      { name: 'Profil Ayarları', route: '/profileSettings' },
      { name: 'Asistan Ayarları', route: '/asistantSettings' },
      {
        name: 'E-Posta Sunucu Ayarları',
        route: '/mailSettings',
        requiredPermissions: ['SMTP_VIEW']
      }
    ]
  }
]);
</script>

<style scoped>
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.3s ease;
  opacity: 1;
  transform: translateX(0);
}

.submenu-enter-from,
.submenu-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.menu-item-enter-active,
.menu-item-leave-active {
  transition: all 0.5s;
}
.menu-item-enter-from,
.menu-item-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.menu-item-move {
  transition: transform 0.5s;
}

/* Styles for disabled items */
.filter-grayscale {
  filter: grayscale(100%);
}

/* Add a hover effect for menu items */
.filter-blue {
  filter: brightness(0) saturate(100%) invert(36%) sepia(98%) saturate(1990%) hue-rotate(222deg)
    brightness(99%) contrast(101%);
}
</style>
